import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import { ScrollReveal } from '@/components/ScrollReveal';
import { 
  ArrowLeft,
  Heart,
  Share2,
  Wrench,
  CheckCircle,
  Shield,
  MapPin,
  Calendar,
  Eye,
  Star,
  Zap,
  Battery,
  Gauge,
  Weight,
  Cog,
  Ruler,
  Clock,
  User,
  Phone,
  Mail,
  ZoomIn,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const VeloDetail = () => {
  const { id } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  // Données détaillées du vélo (normalement récupérées via API)
  const bikeData = {
    'velo-001': {
      id: 'velo-001',
      name: 'Elops 500E',
      brand: 'Decathlon',
      fullName: 'Decathlon Elops 500E',
      year: '2022',
      images: [
        './public/images/velo_ville_classique_reconditionne_20260211_125242_20260225_142435.png',
        './public/images/velo_ville_classique_reconditionne_20260211_125242_20260225_142435.png',
        './public/images/velo_ville_classique_reconditionne_20260211_125242_20260225_142435.png'
      ],
      condition: 'excellent',
      conditionLabel: 'Excellent',
      conditionDescription: 'Vélo en excellent état, très peu utilisé. Aucune trace d\'usure visible.',
      bikeType: 'ville',
      bikeTypeLabel: 'Ville électrique',
      wheelSize: '28',
      frameSize: 'M',
      frameSizeDetail: '54cm (1m65-1m80)',
      price: 280,
      originalPrice: 450,
      discount: 38,
      available: true,
      deliveryDate: '3 mars',
      
      // Caractéristiques techniques complètes
      specs: {
        frame: 'Aluminium 6061, cadre ouvert',
        motor: 'Moteur roue arrière 250W',
        battery: '250 Wh, amovible',
        transmission: '7 vitesses Shimano Tourney',
        brakes: 'V-Brake avant et arrière',
        wheels: '28" avec pneus ville',
        weight: '22 kg',
        maxSpeed: '25 km/h',
        autonomy: '40-60 km',
        chargingTime: '4-5 heures',
        suspension: 'Aucune',
        lighting: 'Éclairage LED avant/arrière',
        accessories: 'Garde-boue, porte-bagages, béquille'
      },
      
      // État détaillé
      condition_details: {
        usage: '2,450 km depuis 2022',
        previous_owner: 'Professionnel',
        battery_cycles: '28 cycles de charge',
        replaced_parts: ['Plaquettes de frein', 'Câbles de transmission'],
        checked_points: [
          'Structure du cadre',
          'Système électrique',
          'Freinage',
          'Transmission',
          'Roues et pneus',
          'Éclairage'
        ],
        last_service: '15 février 2026'
      },
      
      // Description détaillée
      description: 'Vélo électrique urbain parfait pour vos trajets quotidiens. Confortable et fiable, idéal pour les déplacements en ville sur terrain plat. Équipé d\'un moteur silencieux et d\'une batterie longue durée.',
      target_user: 'Parfait pour les trajets domicile-travail de 5-15 km en ville, utilisateurs débutants en vélo électrique.',
      
      // Guide de taille
      size_guide: {
        current_size: 'M (54cm)',
        suitable_height: '1m65 - 1m80',
        inseam_range: '78 - 85 cm',
        bike_type_multiplier: 0.65
      }
    },
    'velo-002': {
      id: 'velo-002',
      name: 'Reaction Hybrid Pro',
      brand: 'Cube',
      fullName: 'Cube Reaction Hybrid Pro',
      year: '2023',
      images: [
        './public/images/vtt_tout_terrain_reconditionne_20260211_125856_20260225_142432.png',
        './public/images/vtt_tout_terrain_reconditionne_20260211_125856_20260225_142432.png',
        './public/images/vtt_tout_terrain_reconditionne_20260211_125856_20260225_142432.png'
      ],
      condition: 'very-good',
      conditionLabel: 'Très bon',
      conditionDescription: 'Vélo en très bon état avec quelques traces d\'usage normal. Parfaitement fonctionnel.',
      bikeType: 'vtt',
      bikeTypeLabel: 'VTT électrique',
      wheelSize: '27.5',
      frameSize: 'L',
      frameSizeDetail: '58cm (1m75-1m85)',
      price: 1320,
      originalPrice: 2200,
      discount: 40,
      available: true,
      deliveryDate: '5 mars',
      
      specs: {
        frame: 'Aluminium Cube Advanced Twin Mold',
        motor: 'Bosch Performance Line CX 65 Nm',
        battery: '500 Wh Bosch PowerTube',
        transmission: '10 vitesses Shimano Deore',
        brakes: 'Freins à disque hydrauliques Shimano',
        wheels: '27.5" avec pneus VTT',
        weight: '24 kg',
        maxSpeed: '25 km/h',
        autonomy: '60-90 km',
        chargingTime: '4-6 heures',
        suspension: 'Fourche suspendue 120mm',
        lighting: 'Éclairage Supernova',
        accessories: 'Porte-bidon, protection chaîne'
      },
      
      condition_details: {
        usage: '1,890 km depuis 2023',
        previous_owner: 'Particulier',
        battery_cycles: '45 cycles de charge',
        replaced_parts: ['Pneus', 'Chaîne'],
        checked_points: [
          'Structure du cadre',
          'Système électrique Bosch',
          'Freinage hydraulique',
          'Transmission Shimano',
          'Suspension avant',
          'Roues et pneus'
        ],
        last_service: '18 février 2026'
      },
      
      description: 'VTT électrique performant pour vos aventures en montagne et sentiers techniques. Suspension avant, freins hydrauliques et moteur puissant pour gravir tous les dénivelés.',
      target_user: 'Idéal pour les sorties VTT de 20-40 km avec dénivelé important, cyclistes expérimentés.',
      
      size_guide: {
        current_size: 'L (58cm)',
        suitable_height: '1m75 - 1m85',
        inseam_range: '85 - 92 cm',
        bike_type_multiplier: 0.57
      }
    }
    // Ajouter les autres vélos...
  };

  const bike = bikeData[id as keyof typeof bikeData];

  if (!bike) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Vélo non trouvé</h1>
          <Link to="/velos-occasion">
            <Button>Retour à la liste</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % bike.images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + bike.images.length) % bike.images.length);
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return 'bg-green-50 text-green-700 border-green-200';
      case 'very-good': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'good': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50/50 py-4 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/velos-occasion" className="hover:text-primary flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />
              Vélos reconditionnés
            </Link>
            <span>/</span>
            <span className="text-gray-900">{bike.fullName}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* ZONE 1 - GALERIE */}
          <div className="space-y-4">
            <div className="relative bg-gray-50 rounded-xl overflow-hidden">
              <img 
                src={bike.images[currentImageIndex]} 
                alt={bike.fullName}
                className="w-full h-96 object-cover"
              />
              
              {/* Navigation images */}
              {bike.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              
              {/* Badge état */}
              <div className="absolute top-4 left-4">
                <Badge className={`${getConditionColor(bike.condition)} border font-medium`}>
                  {bike.conditionLabel}
                </Badge>
              </div>
              
              {/* Badge réduction */}
              <div className="absolute top-4 right-4">
                <Badge className="bg-red-500 text-white border-0 font-bold">
                  -{bike.discount}%
                </Badge>
              </div>
            </div>
            
            {/* Miniatures */}
            {bike.images.length > 1 && (
              <div className="flex gap-2">
                {bike.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentImageIndex ? 'border-primary' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ZONE 2 - INFORMATIONS PRINCIPALES */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs">
                  {bike.bikeTypeLabel}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {bike.wheelSize}"
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {bike.year}
                </Badge>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {bike.fullName}
              </h1>
              
              <p className="text-gray-600 mb-4">
                {bike.description}
              </p>
            </div>

            {/* Prix */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-2">
                <span className="text-4xl font-bold text-primary">
                  {bike.price}€
                </span>
                <div className="text-right">
                  <div className="text-lg text-gray-500 line-through">
                    {bike.originalPrice}€
                  </div>
                  <div className="text-sm text-red-600 font-medium">
                    Économie : {bike.originalPrice - bike.price}€
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Disponible</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Livraison dès le {bike.deliveryDate}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button size="lg" className="w-full bg-primary hover:bg-primary/90 font-semibold">
                  <Heart className="h-5 w-5 mr-2" />
                  Réserver ce vélo
                </Button>
                
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => document.getElementById('essayer-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Essayer avant d'acheter
                </Button>
              </div>
            </div>

            {/* Informations rapides */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Ruler className="h-6 w-6 mx-auto mb-2 text-gray-600" />
                <div className="font-medium text-gray-900">{bike.frameSizeDetail}</div>
                <div className="text-sm text-gray-600">Taille cadre</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <Weight className="h-6 w-6 mx-auto mb-2 text-gray-600" />
                <div className="font-medium text-gray-900">{bike.specs.weight}</div>
                <div className="text-sm text-gray-600">Poids</div>
              </div>
            </div>
          </div>
        </div>

        {/* ZONE 3 - CARACTÉRISTIQUES TECHNIQUES */}
        <div className="mt-16">
          <ScrollReveal>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cog className="h-5 w-5" />
                  Caractéristiques techniques
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Cadre</span>
                      <span className="font-medium">{bike.specs.frame}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Moteur</span>
                      <span className="font-medium">{bike.specs.motor}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Batterie</span>
                      <span className="font-medium">{bike.specs.battery}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Transmission</span>
                      <span className="font-medium">{bike.specs.transmission}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Freins</span>
                      <span className="font-medium">{bike.specs.brakes}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Roues</span>
                      <span className="font-medium">{bike.specs.wheels}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Vitesse max</span>
                      <span className="font-medium">{bike.specs.maxSpeed}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Autonomie</span>
                      <span className="font-medium">{bike.specs.autonomy}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Temps de charge</span>
                      <span className="font-medium">{bike.specs.chargingTime}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Accessoires</span>
                      <span className="font-medium">{bike.specs.accessories}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>

        {/* ZONE 4 - ÉTAT DÉTAILLÉ */}
        <div className="mt-8">
          <ScrollReveal>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  État détaillé du vélo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Historique</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{bike.condition_details.usage}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">Propriétaire précédent : {bike.condition_details.previous_owner}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Battery className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">Batterie : {bike.condition_details.battery_cycles}</span>
                      </div>
                    </div>
                    
                    <h4 className="font-semibold text-gray-900 mb-4 mt-6">Pièces remplacées</h4>
                    <ul className="space-y-2">
                      {bike.condition_details.replaced_parts.map((part, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {part}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Points contrôlés</h4>
                    <ul className="space-y-2">
                      {bike.condition_details.checked_points.map((point, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-primary" />
                          {point}
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mt-6 p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        <span className="font-medium text-green-900">Dernier contrôle</span>
                      </div>
                      <p className="text-sm text-green-700">
                        {bike.condition_details.last_service}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>

        {/* ZONE 5 - PROCESSUS DE RECONDITIONNEMENT */}
        <div className="mt-8">
          <ScrollReveal>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Notre processus de reconditionnement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-5 gap-6">
                  {[
                    { step: 1, title: 'Inspection structure', desc: 'Contrôle complet du cadre et des composants' },
                    { step: 2, title: 'Remplacement pièces', desc: 'Changement des éléments d\'usure' },
                    { step: 3, title: 'Réglages précis', desc: 'Ajustement de tous les systèmes' },
                    { step: 4, title: 'Test dynamique', desc: 'Validation sur route' },
                    { step: 5, title: 'Contrôle final', desc: 'Vérification qualité' }
                  ].map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="font-bold text-primary">{item.step}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">{item.title}</h4>
                      <p className="text-xs text-gray-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>

        {/* ZONE 6 - GARANTIE & SÉCURITÉ */}
        <div className="mt-8">
          <ScrollReveal>
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Garantie 1 an</h3>
                  <p className="text-gray-600 text-sm">Couverture complète pièces et main d'œuvre</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Vélo assuré en test</h3>
                  <p className="text-gray-600 text-sm">Couverture complète pendant l'essai</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6 text-center">
                  <Phone className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="font-bold text-gray-900 mb-2">Service après-vente</h3>
                  <p className="text-gray-600 text-sm">Support technique et conseils</p>
                </CardContent>
              </Card>
            </div>
          </ScrollReveal>
        </div>

        {/* ZONE 7 - ESSAYER AVANT D'ACHETER */}
        <div className="mt-8" id="essayer-section">
          <ScrollReveal>
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Essayez avant d'acheter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Service disponible uniquement :</h4>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>Île de Ré</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>La Rochelle</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        <span>Rayon de 45 minutes autour de La Rochelle</span>
                      </li>
                    </ul>
                    
                    <p className="text-gray-600 text-sm mb-4">
                      Testez ce vélo dans des conditions réelles avant de prendre votre décision. 
                      Aucune obligation d'achat.
                    </p>
                  </div>
                  
                  <div>
                    <Button size="lg" className="w-full mb-4">
                      <Calendar className="h-5 w-5 mr-2" />
                      Prendre rendez-vous
                    </Button>
                    
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Ou contactez-nous directement :</p>
                      <div className="flex justify-center gap-4">
                        <Button variant="outline" size="sm">
                          <Phone className="h-4 w-4 mr-2" />
                          Appeler
                        </Button>
                        <Button variant="outline" size="sm">
                          <Mail className="h-4 w-4 mr-2" />
                          Email
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>

        {/* GUIDE DE TAILLE INTÉGRÉ */}
        <div className="mt-8">
          <ScrollReveal>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ruler className="h-5 w-5" />
                  Choisir la bonne taille
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Ce vélo vous convient-il ?</h4>
                    <div className="bg-primary/5 rounded-lg p-4 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary mb-1">
                          {bike.size_guide.current_size}
                        </div>
                        <div className="text-sm text-gray-600">Taille de ce vélo</div>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Taille recommandée :</span>
                        <span className="font-medium">{bike.size_guide.suitable_height}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Entrejambe :</span>
                        <span className="font-medium">{bike.size_guide.inseam_range}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Comment calculer votre taille ?</h4>
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <p className="text-sm font-medium mb-2">Formule pour {bike.bikeTypeLabel} :</p>
                      <p className="text-sm text-gray-600 mb-2">
                        Taille cadre (cm) = Entrejambe (cm) × {bike.size_guide.bike_type_multiplier}
                      </p>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <p className="mb-2"><strong>Comment mesurer votre entrejambe :</strong></p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Tenez-vous debout, dos contre un mur</li>
                        <li>Placez un livre entre vos jambes</li>
                        <li>Mesurez du sol au sommet du livre</li>
                        <li>Utilisez cette mesure dans la formule</li>
                      </ol>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => setShowSizeGuide(!showSizeGuide)}
                    >
                      {showSizeGuide ? 'Masquer' : 'Voir'} le tableau des tailles
                    </Button>
                  </div>
                </div>
                
                {showSizeGuide && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4">Tableau des correspondances</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 font-semibold">Taille</th>
                            <th className="text-left py-2 font-semibold">Cadre (cm)</th>
                            <th className="text-left py-2 font-semibold">Entrejambe (cm)</th>
                            <th className="text-left py-2 font-semibold">Taille (m)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 font-medium">XS</td>
                            <td className="py-2">47-50</td>
                            <td className="py-2">72-77</td>
                            <td className="py-2">1m50-1m65</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 font-medium">S</td>
                            <td className="py-2">51-54</td>
                            <td className="py-2">78-83</td>
                            <td className="py-2">1m60-1m70</td>
                          </tr>
                          <tr className={`border-b border-gray-100 ${bike.frameSize === 'M' ? 'bg-primary/5' : ''}`}>
                            <td className="py-2 font-medium">M</td>
                            <td className="py-2">55-58</td>
                            <td className="py-2">84-89</td>
                            <td className="py-2">1m65-1m80</td>
                          </tr>
                          <tr className={`border-b border-gray-100 ${bike.frameSize === 'L' ? 'bg-primary/5' : ''}`}>
                            <td className="py-2 font-medium">L</td>
                            <td className="py-2">59-62</td>
                            <td className="py-2">90-95</td>
                            <td className="py-2">1m75-1m85</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 font-medium">XL</td>
                            <td className="py-2">63-66</td>
                            <td className="py-2">96-101</td>
                            <td className="py-2">1m80-1m95</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </ScrollReveal>
        </div>
      </div>

      <Footer />
      <FloatingFAQ />
    </div>
  );
};

export default VeloDetail;